import type { AxiosError, AxiosResponse } from "axios";
import type {
  ExclusionBulkUploadResult,
  ExclusionRuleDetail,
  ExclusionRuleInput,
  ExclusionRulesDeleteResult,
  ExclusionRulesListQuery,
  ExclusionRulesListResponse,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler, generateUrlString } from "utilz/helpers";

/**
 * `/v1/servicedesk/channels/email/exclusion-rules/*` — Email Exclusion Rules.
 * Envelope: `{ success, statusCode, data, message }`. Business errors (400 /
 * 404 / 409) arrive over HTTP 200 with `success: false`, so the body is the
 * source of truth, not the HTTP status.
 *
 * A rule IS one condition (single-condition rebuild, 2026-10-15): one id,
 * `mapping_id`, used for list selection, detail, PATCH and delete.
 *
 * Contract: .claude/projects/control-center/email-servers/email-servers-v2/
 *           email-exclusions-api-doc.md
 * Design:   .claude/projects/control-center/email-servers/email-servers-v2/design/
 */
const BASE_URL = "/v1/servicedesk/channels/email/exclusion-rules";

const isOk = (
  responseData: { success?: boolean; statusCode?: number } | null | undefined,
) => responseData?.success === true && responseData?.statusCode === 200;

/** Drops unset params so the backend applies its own defaults. */
const buildListParams = (
  query: ExclusionRulesListQuery,
): Record<string, string> => {
  const entries: Array<[string, string | number | undefined]> = [
    ["page", query.page],
    ["per_page", query.per_page],
    ["sort_by", query.sort_by],
    ["sort_dir", query.sort_dir],
    ["condition_type", query.condition_type],
    ["action", query.action],
    ["applies_to", query.applies_to],
    ["search", query.search],
  ];
  const params: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (value !== undefined && value !== "") params[key] = String(value);
  }
  return params;
};

/** GET /exclusion-rules — one row per rule. */
export const getExclusionRules = async (
  query: ExclusionRulesListQuery,
): Promise<ExclusionRulesListResponse> => {
  try {
    const url = await generateUrlString({
      baseUrl: BASE_URL,
      data: buildListParams(query),
    });
    const response = await axiosClient.get(url);
    const responseData = response?.data;
    if (isOk(responseData)) {
      const body = responseData.data ?? {};
      return {
        data: Array.isArray(body.data) ? body.data : [],
        total: Number(body.total ?? 0),
        page: Number(body.page ?? query.page),
        per_page: Number(body.per_page ?? query.per_page),
      };
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/** GET /exclusion-rules/:mapping_id — one rule. */
export const getExclusionRule = async (
  ruleId: string,
): Promise<ExclusionRuleDetail> => {
  try {
    const response = await axiosClient.get(`${BASE_URL}/${ruleId}`);
    const responseData = response?.data;
    if (isOk(responseData)) {
      return responseData.data as ExclusionRuleDetail;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/** POST /exclusion-rules — creates ONE rule; several conditions = several calls. */
export const createExclusionRule = async (payload: ExclusionRuleInput) => {
  try {
    const response = await axiosClient.post(BASE_URL, payload);
    const responseData = response?.data;
    if (isOk(responseData)) {
      return responseData.data ?? null;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * PATCH /exclusion-rules/:mapping_id — full replace of this one rule: send
 * every field (type, value, action, scope) on every call, not a diff.
 */
export const updateExclusionRule = async (
  ruleId: string,
  payload: ExclusionRuleInput,
) => {
  try {
    const response = await axiosClient.patch(`${BASE_URL}/${ruleId}`, payload);
    const responseData = response?.data;
    if (isOk(responseData)) {
      return responseData.data ?? null;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * DELETE /exclusion-rules/bulk-delete — body `{ ids }` (rule `mapping_id`s).
 * Not the bare base path: that one is caught by the mailbox
 * `DELETE /channels/email/:mappingId` route and deletes nothing.
 */
export const deleteExclusionRules = async (
  ids: string[],
): Promise<ExclusionRulesDeleteResult | null> => {
  try {
    const response = await axiosClient.delete(`${BASE_URL}/bulk-delete`, {
      data: { ids },
    });
    const responseData = response?.data;
    if (isOk(responseData)) {
      return (responseData.data as ExclusionRulesDeleteResult) ?? null;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * POST /exclusion-rules/bulk-upload — multipart, field `file`.
 * Any invalid row rejects the whole file: the thrown error carries
 * `data.errors[{ row, field, message }]`. Duplicates are skipped, not failed.
 */
export const bulkUploadExclusionRules = async (
  file: File,
): Promise<ExclusionBulkUploadResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axiosClient.post(
      `${BASE_URL}/bulk-upload`,
      formData,
    );
    const responseData = response?.data;
    if (isOk(responseData)) {
      return responseData.data as ExclusionBulkUploadResult;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
